import {
  DocumentAnalysisSituation,
  DocumentAnalysisSituationKind,
} from '@interfaces/accounting-data-standardization/responses/document-analysis-situation.response';

/** Tom visual da tag da situação (cores do protótipo aprovado, card 3700). */
export type AnalysisSituationTone = 'success' | 'warning' | 'neutral' | 'danger' | 'brand';

export interface AnalysisSituationTag {
  label: string;
  tone: AnalysisSituationTone;
}

/** Pedaço da frase da situação: texto corrido ou o nome do outro documento, que vira link. */
export interface AnalysisSituationMessagePart {
  text: string;
  isLink: boolean;
}

/**
 * Tag da coluna "Situação na análise". Só o rótulo e a cor ficam no front; a frase vem pronta do backend. `Pending`
 * não tem tag (só a frase, quando houver). `Effective` usa o próprio período ("Vale para jan–set 2026").
 */
export function analysisSituationTag(
  situation: DocumentAnalysisSituation | null | undefined
): AnalysisSituationTag | null {
  if (!situation) return null;
  const labels: Record<DocumentAnalysisSituationKind, AnalysisSituationTag | null> = {
    Effective: {
      label: situation.periodLabel ? `Vale para ${situation.periodLabel}` : 'Vale na análise',
      tone: 'success',
    },
    // Sem `effectivePeriodLabel`, o documento validado não vale em mês/ano nenhum: não é "em parte".
    PartiallyEffective: situation.effectivePeriodLabel?.trim()
      ? { label: 'Vale só em parte', tone: 'warning' }
      : { label: 'Não entra na análise', tone: 'warning' },
    PeriodCovered: { label: 'Período já coberto', tone: 'warning' },
    Superseded: { label: 'Substituído', tone: 'neutral' },
    SourceFileMissing: { label: 'Arquivo não está no Data Room', tone: 'danger' },
    OnlyDocumentForYear: {
      label: situation.periodLabel
        ? `Único documento de ${situation.periodLabel}`
        : 'Único documento do ano',
      tone: 'brand',
    },
    Pending: null,
  };
  return labels[situation.kind] ?? null;
}

/**
 * Quebra a frase do backend em volta do nome do documento que vale no lugar deste, para o nome virar link. Sem o id do
 * outro documento (planilha antiga, ou não achado) ou sem o nome dentro da frase, devolve a frase inteira sem link.
 */
export function analysisSituationMessageParts(
  situation: DocumentAnalysisSituation | null | undefined
): AnalysisSituationMessagePart[] {
  const message = situation?.message?.trim();
  if (!message) return [];

  const name = situation?.coveredByName?.trim();
  const index = name && situation?.coveredByExtractionId ? message.indexOf(name) : -1;
  if (!name || index < 0) return [{ text: message, isLink: false }];

  return [
    { text: message.slice(0, index), isLink: false },
    { text: name, isLink: true },
    { text: message.slice(index + name.length), isLink: false },
  ].filter(part => part.text.length > 0);
}
